import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box
} from '@mui/material';

import FMSModal from 'src/common/FMSModal';
import Loader from 'src/common/Loader';
import CustomPagination from 'src/common/CustomPagination';
import FMSToolbar from 'src/common/FMSToolbar';
import { APIService } from 'src/api/api';

function DispenserLinkedCardsModel({
  open,
  onClose,
  dispenserId,
  fetchCards
}) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (!open || !dispenserId) return;

    setLoading(true);

    fetchCards(
      dispenserId,
      limit,
      page + 1,
      searchText ? { search: searchText } : {}
    )
      .then((res) => {
        setCards(res?.result?.results || []);
        setTotalCount(res?.result?.count || 0);
      })
      .catch(() => {
        setCards([]);
        setTotalCount(0);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [fetchCards, open, dispenserId, limit, page, searchText]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPage(0);
  };

  return (
    <FMSModal open={open} maxWidth="md" onClose={onClose}>
      <DialogTitle>LINKED CARDS</DialogTitle>
      <Divider />

      <DialogContent>
        <FMSToolbar
          placeholder="Search Card UID / Vehicle Number"
          hideAction
          onSearch={handleSearch}
        />

        {loading ? (
          <Loader />
        ) : (
          <>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Card UID</strong></TableCell>
                    <TableCell><strong>Vehicle Number</strong></TableCell>
                    <TableCell><strong>Limit Type</strong></TableCell>
                    <TableCell><strong>Card Category</strong></TableCell>
                    <TableCell><strong>Limit Quantity</strong></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {cards.length > 0 ? (
                    cards.map((card) => (
                      <TableRow key={card.id}>
                        <TableCell>{card.uid}</TableCell>
                        <TableCell>{card.vehicle_number || '-'}</TableCell>
                        <TableCell>{card.fill_limit_type || '-'}</TableCell>
                        <TableCell>{card.card_category || '-'}</TableCell>
                        <TableCell>{card.limit_quantity ?? '-'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No cards found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalCount > 0 && (
              <Box sx={{ mt: 2 }}>
                <CustomPagination
                  page={page}
                  rowsPerPage={limit}
                  count={totalCount}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleLimitChange}
                  showRowsPerPage
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={onClose}>Close</Button>
      </DialogActions>
    </FMSModal>
  );
}

DispenserLinkedCardsModel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dispenserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fetchCards: PropTypes.func
};

DispenserLinkedCardsModel.defaultProps = {
  fetchCards: APIService.getDispenserLinkedCards
};

export default DispenserLinkedCardsModel;
